/**
 * 수입결의 공통 워크스페이스
 * - register: 마스터+거래처디테일 · 예산축 코드조회
 * - approve: 마스터+디테일 조인 그리드 승인/반려
 * - 승인 시 env income.receiptOnIncomeApprove → 수납결의 자동생성
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadBalances, filterBalances } from 'budget/budgetBalanceStore';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import { loadEnvSettings } from 'system/envSettingsStore';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  STATUS,
  STATUS_OPTIONS,
  createResolutionUid,
  createResolutionDetailUid,
  emptyResolutionMaster,
  emptyResolutionVendorRow,
  loadResolutionBundle,
  saveResolutionBundle,
  loadSources,
  filterSources,
  filterResolutionsForRegister,
  loadJoinedForApprove,
  requestResolutionApproval,
  approveResolution,
  rejectResolution,
  nextResolutionNo,
  statusLabel,
  todayYmd,
} from 'income/incomeStore';

const LIST_GRID_ID = 'income-resolution-list';
const DETAIL_GRID_ID = 'income-resolution-vendor';
const APPROVE_GRID_ID = 'income-resolution-approve';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const MasterToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 0;
  flex-shrink: 0;
`;

const BalanceBtn = styled.button`
  padding: 2px 8px;
  min-height: 26px;
  font-size: ${theme.typography.fontSize.xs};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background.secondary};
  cursor: pointer;
  &:hover {
    background: ${theme.colors.background.hover};
  }
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

function emptySearch(partial = {}) {
  return { fiscalYear: getCurrentFiscalYear(), status: '', ...partial };
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
function IncomeResolutionWorkspaceInner({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const searchDefaults = useMemo(
    () => emptySearch({ status: isApprove ? STATUS.REQUESTED : '' }),
    [isApprove]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyResolutionMaster({ resolutionDate: todayYmd(), fiscalYear: searchDefaults.fiscalYear }),
      sourcePicker: '',
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const listRef = useRef(null);
  const detailRef = useRef(null);
  const approveRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleVendors, setBundleVendors] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [lookup, setLookup] = useState({ open: false, type: '' });

  const masterStatus = useWatch({ control, name: 'status' });
  const masterReadonly =
    isApprove ||
    (masterStatus &&
      masterStatus !== STATUS.DRAFT &&
      masterStatus !== STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const { handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isApprove) return '승인 화면에서는 추가할 수 없습니다.';
      if (id === DETAIL_GRID_ID && !selectedUidRef.current) {
        return '결의를 선택하거나 신규 추가하세요.';
      }
      return null;
    },
    guardDelete: () => (isApprove ? '승인 화면에서는 삭제할 수 없습니다.' : null),
  });

  const dimsFromSearch = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const receiptOnApprove = useMemo(() => {
    const env = loadEnvSettings(tenantCode);
    return env?.income?.receiptOnIncomeApprove === 'Y' ? 'Y' : 'N';
  }, [tenantCode]);

  const enrichListRow = useCallback(
    (row) => ({
      ...row,
      statusNm: statusLabel(row.status),
    }),
    []
  );

  const loadSourcePicker = useCallback(async () => {
    const dims = dimsFromSearch();
    const rows = filterSources(await loadSources(dims), { useYn: 'Y' });
    setSourceOptions(
      rows.map((s) => ({
        value: s.sourceUid,
        label: `${s.sourceNo} · ${s.vendorName || ''} · ${Number(s.amount || 0).toLocaleString()}원`,
        ...s,
      }))
    );
  }, [dimsFromSearch]);

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 110 }),
      hiCol.roNumberColumn({ header: '결의금액', field: 'resolutionAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const detailColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 90, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 120, editable: !masterReadonly }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140, editable: !masterReadonly }),
    ],
    [masterReadonly]
  );

  const approveColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roTextColumn({ header: '거래처', field: 'vendorName', size: 110 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '결의금액', field: 'resolutionAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const detailGrid = useHiGridHook({
    gridRef: detailRef,
    autoSearch: false,
    enableContextMenu: !isApprove && !masterReadonly,
    confirmCheck: !isApprove && !masterReadonly,
    defaultRow: emptyResolutionVendorRow(),
  });

  const listGrid = useHiGridHook({
    gridRef: listRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const approveGrid = useHiGridHook({
    gridRef: approveRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const loadDetails = useCallback(
    (uid) => {
      const list = (bundleVendors || []).filter((d) => d.resolutionUid === uid && d.mod !== 'D');
      detailRef.current?.setData?.(list);
    },
    [bundleVendors]
  );

  const loadMasterToForm = useCallback(
    (row) => {
      if (!row) {
        reset({
          ...getValues(),
          ...emptyResolutionMaster({ fiscalYear: getValues('fiscalYear'), resolutionDate: todayYmd() }),
          sourcePicker: '',
        });
        detailRef.current?.setData?.([]);
        return;
      }
      reset({
        ...getValues(),
        ...row,
        statusNm: statusLabel(row.status),
        sourcePicker: row.sourceUid || '',
      });
      loadDetails(row.resolutionUid);
    },
    [reset, getValues, loadDetails]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    loadSourcePicker();

    if (isApprove) {
      const rows = await loadJoinedForApprove(dims);
      const statusFilter = String(getValues('status') || '').trim();
      const filtered = statusFilter ? rows.filter((r) => r.status === statusFilter) : rows;
      approveRef.current?.setData?.(filtered);
      return;
    }

    const bundle = await loadResolutionBundle(dims);
    const statusFilter = String(getValues('status') || '').trim();
    let masters = filterResolutionsForRegister(bundle.masters, { status: statusFilter || undefined });
    masters = masters.map(enrichListRow);
    setBundleVendors(bundle.vendors);
    listRef.current?.setData?.(masters);
    const first = masters[0];
    setSelectedUid(first?.resolutionUid || '');
    loadMasterToForm(first);
  }, [dimsFromSearch, getValues, isApprove, enrichListRow, loadMasterToForm, loadSourcePicker]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyResolutionMaster({ fiscalYear: searchDefaults.fiscalYear, resolutionDate: todayYmd() }),
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      setSelectedUid(row?.resolutionUid || '');
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = ({ data }) => onListSelect(data);
  }, [listGrid, onListSelect]);

  useEffect(() => {
    approveGrid.onRowClicked = ({ data }) => {
      selectedUidRef.current = data?.resolutionUid || '';
      setSelectedUid(data?.resolutionUid || '');
    };
    approveGrid.onSelectRow = ({ data }) => {
      selectedUidRef.current = data?.resolutionUid || '';
      setSelectedUid(data?.resolutionUid || '');
    };
  }, [approveGrid]);

  const collectVendors = useCallback(() => {
    const uid = selectedUidRef.current;
    return (detailRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        resolutionUid: uid,
        detailUid: r.detailUid || createResolutionDetailUid(),
      }));
  }, []);

  const handleNew = useCallback(() => {
    if (isApprove) return;
    const uid = createResolutionUid();
    const row = enrichListRow(
      emptyResolutionMaster({
        resolutionUid: uid,
        fiscalYear: dimsFromSearch().fiscalYear,
        resolutionDate: todayYmd(),
        status: STATUS.DRAFT,
        registerDate: todayYmd(),
        mod: 'A',
      })
    );
    setSelectedUid(uid);
    selectedUidRef.current = uid;
    reset({ ...getValues(), ...row, sourcePicker: '' });
    detailRef.current?.setData?.([]);
  }, [isApprove, enrichListRow, dimsFromSearch, reset, getValues]);

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장할 수 없습니다.');
      return;
    }
    const dims = dimsFromSearch();
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createResolutionUid();
      setSelectedUid(uid);
      selectedUidRef.current = uid;
    }

    const form = getValues();
    const bundle = await loadResolutionBundle(dims);

    let masterDraft = {
      ...emptyResolutionMaster(),
      ...form,
      resolutionUid: uid,
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || STATUS.DRAFT,
      mod: form.mod || 'U',
    };

    if (!masterDraft.resolutionNo) {
      masterDraft.resolutionNo = nextResolutionNo(bundle.masters);
    }

    const picker = String(form.sourcePicker || '').trim();
    if (picker) {
      const src = sourceOptions.find((o) => o.value === picker);
      if (src) {
        masterDraft.sourceUid = src.sourceUid;
        masterDraft.sourceNo = src.sourceNo;
      }
    }

    let masters = [...(bundle.masters || [])];
    const idx = masters.findIndex((m) => m.resolutionUid === uid);
    if (idx >= 0) masters[idx] = { ...masters[idx], ...masterDraft, mod: 'U' };
    else masters.push({ ...masterDraft, mod: 'A' });

    let vendors = (bundle.vendors || []).filter((d) => d.resolutionUid !== uid);
    vendors = [...vendors, ...collectVendors()];

    const saved = await saveResolutionBundle(dims, masters, vendors);
    setBundleVendors(saved.vendors);
    listRef.current?.setData?.(saved.masters.filter((m) => m.mod !== 'D').map(enrichListRow));
    loadMasterToForm(saved.masters.find((m) => m.resolutionUid === uid));
    showAlert('저장했습니다.', 'success');
  }, [isApprove, dimsFromSearch, getValues, sourceOptions, collectVendors, enrichListRow, loadMasterToForm]);

  const handleRequest = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = requestResolutionApproval(bundle.masters, bundle.vendors, uid);
    await saveResolutionBundle(dims, result.masters, result.vendors);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [isApprove, handleSave, dimsFromSearch, handleSearch]);

  const handleApprove = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.resolutionUid === selectedUidRef.current);
    if (!target?.resolutionUid) {
      showAlert('승인할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm(
      receiptOnApprove === 'Y'
        ? '승인할까요? (환경설정: 수납결의 자동생성)'
        : '승인할까요?'
    );
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = await approveResolution(bundle.masters, bundle.vendors, target.resolutionUid, {
      receiptOnApprove,
      dims,
    });
    await saveResolutionBundle(dims, result.masters, result.vendors);
    await handleSearch();
    if (receiptOnApprove === 'Y' && result.receiptBundle?.stub) {
      showAlert(`승인했습니다. 수납결의 ${result.receiptBundle.stub.receiptNo} 자동생성 (현금주의).`, 'success');
    } else {
      showAlert('승인했습니다.', 'success');
    }
  }, [isApprove, dimsFromSearch, handleSearch, receiptOnApprove]);

  const handleReject = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.resolutionUid === selectedUidRef.current);
    if (!target?.resolutionUid) {
      showAlert('반려할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = rejectResolution(bundle.masters, bundle.vendors, target.resolutionUid);
    await saveResolutionBundle(dims, result.masters, result.vendors);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const dispatchVendorAdd = useCallback(async () => {
    if (isApprove || masterReadonly || !selectedUidRef.current) return;
    detailRef.current?.setData?.([
      ...(detailRef.current?.getData?.() || []),
      emptyResolutionVendorRow({
        detailUid: createResolutionDetailUid(),
        resolutionUid: selectedUidRef.current,
        mod: 'A',
      }),
    ]);
  }, [isApprove, masterReadonly]);

  const onSourcePick = useCallback(
    (e) => {
      const val = e?.target?.value ?? e;
      const src = sourceOptions.find((o) => o.value === val);
      if (!src) return;
      setValue('sourceUid', src.sourceUid);
      setValue('sourceNo', src.sourceNo);
    },
    [sourceOptions, setValue]
  );

  // ─── 코드조회 (현액·부서·사업·과목) ─────────────────────────
  const lookupConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const fy = dims.fiscalYear || getCurrentFiscalYear();

    if (lookup.type === 'balance') {
      const all = loadBalances({ tenantCode, fiscalYear: fy });
      let rows = filterBalances(all, { inoutDiv: '수입' });
      if (!rows.length) rows = all;
      return {
        title: '현액 조회 (수입)',
        rows: rows.map((r) => ({
          ...r,
          id: r.balanceUid || `${r.deptCode}-${r.projectCode}-${r.subjectCode}`,
        })),
        columns: [
          hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
          hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 120 }),
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 100 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 120 }),
          hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
          hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['deptName', 'projectName', 'subjectName', 'fundName'],
        },
      };
    }

    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회',
        rows: rows.map((r) => ({ ...r, id: r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 90 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 140 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
      };
    }

    if (lookup.type === 'project') {
      const rows = loadOrSeedProjects({ tenantCode, fiscalYear: fy });
      return {
        title: '사업 조회',
        rows: rows.map((r) => ({ ...r, id: r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 100 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName'] },
      };
    }

    if (lookup.type === 'subject') {
      const rows = loadSubjectsByFilter({ tenantCode, fiscalYear: fy, inoutDiv: '수입' });
      return {
        title: '예산과목 조회 (수입)',
        rows: rows.map((r) => ({
          ...r,
          id: r.subjectUid || r.subjectCode,
        })),
        columns: [
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 110 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['subjectDispCode', 'subjectName'] },
      };
    }

    return { title: '', rows: [], columns: [], enableSearch: false };
  }, [lookup.type, dimsFromSearch, tenantCode]);

  const applyLookup = useCallback(
    (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return;

      if (lookup.type === 'balance') {
        setValue('balanceUid', r.balanceUid || '');
        setValue('deptCode', r.deptCode || '');
        setValue('deptName', r.deptName || '');
        setValue('projectCode', r.projectCode || '');
        setValue('projectName', r.projectName || '');
        setValue('subjectCode', r.subjectDispCode || r.subjectCode || '');
        setValue('subjectName', r.subjectName || '');
        setValue('fundCode', r.fundCode || '');
        setValue('fundName', r.fundName || '');
        setValue('balanceAmt', r.balanceAmt != null ? Number(r.balanceAmt) : 0);
      } else if (lookup.type === 'dept') {
        setValue('deptCode', r.deptCode || '');
        setValue('deptName', r.deptName || '');
      } else if (lookup.type === 'project') {
        setValue('projectCode', r.projectCode || '');
        setValue('projectName', r.projectName || '');
      } else if (lookup.type === 'subject') {
        setValue('subjectCode', r.subjectDispCode || r.subjectCode || '');
        setValue('subjectName', r.subjectName || '');
      }
      setLookup({ open: false, type: '' });
    },
    [lookup.type, setValue]
  );

  const searchFields = useMemo(
    () => [
      [
        { name: 'fiscalYear', label: '회계연도', type: 'fiscalYear', width: '88px', labelWidth: SEARCH_LABEL_W },
        {
          name: 'status',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...STATUS_OPTIONS],
          width: '120px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const masterFields = useMemo(
    () => [
      [
        { name: 'resolutionNo', label: '결의번호', type: 'text', readOnly: true, width: '90px', labelWidth: '64px' },
        {
          name: 'sourcePicker',
          label: '수입원천',
          type: 'select',
          options: [{ value: '', label: '(선택)' }, ...sourceOptions.map((o) => ({ value: o.value, label: o.label }))],
          width: '220px',
          labelWidth: '64px',
          readOnly: masterReadonly,
          onChange: onSourcePick,
        },
        { name: 'balanceAmt', label: '현액', type: 'number', readOnly: true, width: '100px', labelWidth: '48px' },
      ],
      [
        {
          name: 'deptName',
          label: '부서',
          type: 'text',
          readOnly: true,
          width: '120px',
          labelWidth: '64px',
          showSearch: !masterReadonly,
          searchTitle: '부서 조회',
          onSearch: () => setLookup({ open: true, type: 'dept' }),
        },
        {
          name: 'projectName',
          label: '사업',
          type: 'text',
          readOnly: true,
          width: '140px',
          labelWidth: '48px',
          showSearch: !masterReadonly,
          searchTitle: '사업 조회',
          onSearch: () => setLookup({ open: true, type: 'project' }),
        },
        {
          name: 'subjectName',
          label: '과목',
          type: 'text',
          readOnly: true,
          width: '140px',
          labelWidth: '48px',
          showSearch: !masterReadonly,
          searchTitle: '과목 조회',
          onSearch: () => setLookup({ open: true, type: 'subject' }),
        },
        { name: 'fundName', label: '재원', type: 'text', readOnly: true, width: '100px', labelWidth: '48px' },
      ],
      [
        {
          name: 'resolutionAmt',
          label: '결의금액',
          type: 'number',
          width: '120px',
          labelWidth: '64px',
          readOnly: masterReadonly,
        },
        {
          name: 'resolutionDate',
          label: '결의일',
          type: 'date',
          width: '120px',
          labelWidth: '48px',
          readOnly: masterReadonly,
        },
        { name: 'statusNm', label: '상태', type: 'text', readOnly: true, width: '80px', labelWidth: '48px' },
        {
          name: 'remark',
          label: '적요',
          type: 'text',
          width: '280px',
          labelWidth: '48px',
          readOnly: masterReadonly,
        },
      ],
    ],
    [sourceOptions, masterReadonly, onSourcePick]
  );

  const actionButtons = useMemo(() => {
    const base = [{ id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' }];
    if (isApprove) return base;
    return [
      ...base,
      { id: 'new', label: '추가', icon: '➕', onClick: handleNew, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel}` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ];
  }, [isApprove, handleSearch, handleNew, handleActiveDelete, handleSave, activeLabel]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      return [
        { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }
    return [{ id: 'request', label: '승인요청', onClick: handleRequest }];
  }, [isApprove, handleApprove, handleReject, handleRequest]);

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey={`income-resolution-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {isApprove
              ? `승인요청 결의 선택 후 승인/반려 · 수납자동: ${receiptOnApprove}`
              : '예산축(현액) 조회로 부서·사업·과목·재원 적재'}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
          </Hint>

          {isApprove ? (
            <Panel $flex={1.5}>
              <ActiveGridHeader gridId={APPROVE_GRID_ID}>수입결의 승인 (조인)</ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={approveRef}
                  columns={approveColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableHeaderMenu
                  enableQuickExport
                  exportFileName="수입결의승인"
                  gridId={`income-resolution-approve-${screenMode}`}
                  prefsUserId={prefsUserId}
                />
              </GridBody>
            </Panel>
          ) : (
            <>
              <Panel $flex={0.8}>
                <ActiveGridHeader gridId={LIST_GRID_ID}>결의 목록</ActiveGridHeader>
                <GridBody>
                  <HiGridComponent
                    ref={listRef}
                    columns={listColumns}
                    showRowNumber
                    rowNumberSize={48}
                    enableColumnFilters
                    enableHeaderMenu
                    gridId={`income-resolution-list-${screenMode}`}
                    prefsUserId={prefsUserId}
                  />
                </GridBody>
              </Panel>

              <Panel $flex={0.65}>
                <MasterToolbar>
                  <strong style={{ fontSize: theme.typography.fontSize.sm }}>결의 마스터</strong>
                  {!masterReadonly && (
                    <BalanceBtn type="button" onClick={() => setLookup({ open: true, type: 'balance' })}>
                      현액 조회 ···
                    </BalanceBtn>
                  )}
                </MasterToolbar>
                <FormArea dense fields={masterFields} />
              </Panel>

              <Panel $flex={1.2}>
                <ActiveGridScope
                  gridId={DETAIL_GRID_ID}
                  label="거래처"
                  order={20}
                  onAdd={dispatchVendorAdd}
                  onDelete={async () => detailGrid.handleDelete?.()}
                  canAdd={!masterReadonly}
                  canDelete={!masterReadonly}
                >
                  <ActiveGridHeader gridId={DETAIL_GRID_ID}>거래처 디테일</ActiveGridHeader>
                  <GridBody>
                    <HiGridComponent
                      ref={detailRef}
                      columns={detailColumns}
                      showRowNumber
                      rowNumberSize={48}
                      enableColumnFilters
                      enableContextMenu={!masterReadonly}
                      enableRowEditMenu={!masterReadonly}
                      enableCut={!masterReadonly}
                      enableFindReplace
                      allowReplace={!masterReadonly}
                      enableHeaderMenu
                      enableSelectionStats
                      gridId={`income-resolution-vendor-${screenMode}`}
                      prefsUserId={prefsUserId}
                      headerActive={activeGridId === DETAIL_GRID_ID}
                      {...(masterReadonly ? {} : detailGrid.contextMenuProps || {})}
                    />
                  </GridBody>
                </ActiveGridScope>
              </Panel>
            </>
          )}
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        enableSearch={lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        prefsUserId={prefsUserId}
        sizeKey={`codeLookup-income-${lookup.type}`}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: '' })}
      />
    </PageContainer>
  );
}

export default function IncomeResolutionWorkspace(props) {
  return (
    <ActiveGridProvider>
      <IncomeResolutionWorkspaceInner {...props} />
    </ActiveGridProvider>
  );
}
